import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, ErrorState, Loading } from './states';

interface ShapedClass {
  trafficClass: string;
  dscp: number;
  guaranteePercent: number;
  ceilingPercent: number;
  priority: number;
  guaranteeKbps: number;
  ceilingKbps: number;
  fairQueuing?: boolean;
}

interface ShapingPlan {
  egressKbps: number | null;
  ingressKbps: number | null;
  headroom: number;
  classes: ShapedClass[];
  errors: string[];
  warnings: string[];
}

interface QosView {
  enabled: boolean;
  plan: ShapingPlan;
  notes: string[];
}

const mbps = (kbps: number | null): string =>
  kbps === null ? '—' : `${(kbps / 1000).toFixed(1)} Mbps`;

/**
 * Shaping and queuing for one WAN link.
 *
 * The headroom is shown as a first-class number rather than buried, because it
 * is the setting that decides whether any of the rest has an effect: at line
 * rate the queue forms in the ISP's equipment and the class shares below become
 * decorative.
 *
 * Errors and warnings are kept distinct. An error means shaping cannot be
 * enabled; a warning means it can, but an operator would want to know.
 */
export function QosPanel({ wanLinkId }: { wanLinkId: string }): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync(
    () => api.get<QosView>(`/wan/links/${wanLinkId}/qos`),
    [wanLinkId],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error.message} onRetry={reload} />;
  if (!data) return <ErrorState message={t('errors.notFound')} />;

  const { plan } = data;

  return (
    <div className="card">
      <div className="page-head">
        <h2>{t('qos.title')}</h2>
        <span className={`chip chip--${data.enabled ? 'ok' : 'warn'}`}>
          {data.enabled ? t('qos.enabled') : t('qos.disabled')}
        </span>
      </div>

      {plan.errors.length > 0 ? (
        <Banner variant="error">
          <strong>{t('qos.cannotEnable')}</strong>
          <ul>
            {plan.errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      {plan.warnings.map((w) => (
        <Banner variant="warning" key={w}>
          {w}
        </Banner>
      ))}

      <dl className="kv">
        {/* Shown prominently: this is the setting that decides whether the class
            shares below do anything at all. */}
        <dt>{t('qos.headroom')}</dt>
        <dd>
          {Math.round(plan.headroom * 100)}% {t('qos.ofLineRate')}
        </dd>
        <dt>{t('qos.egress')}</dt>
        <dd>{mbps(plan.egressKbps)}</dd>
        <dt>{t('qos.ingress')}</dt>
        <dd>{mbps(plan.ingressKbps)}</dd>
      </dl>

      {plan.egressKbps === null ? <p className="muted">{t('qos.unknownCapacity')}</p> : null}

      {plan.classes.length > 0 && plan.egressKbps !== null ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('qos.priority')}</th>
                <th aria-label="class" />
                <th>{t('qos.guarantee')}</th>
                <th>{t('qos.ceiling')}</th>
                <th>{t('qos.dscp')}</th>
                <th>{t('qos.fairQueuing')}</th>
              </tr>
            </thead>
            <tbody>
              {plan.classes.map((c) => (
                <tr key={c.trafficClass}>
                  <td>{c.priority}</td>
                  <td>{t(`qos.class.${c.trafficClass}`)}</td>
                  <td>{mbps(c.guaranteeKbps)}</td>
                  <td>{mbps(c.ceilingKbps)}</td>
                  <td>
                    <code>{c.dscp}</code>
                  </td>
                  {/* Absent is treated as on, matching the shared default: fair
                      queuing is nearly always wanted and its absence is the
                      surprising case. */}
                  <td>{c.fairQueuing === false ? '—' : '✓'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <h3>{t('qos.notes')}</h3>
      <ul className="muted">
        {data.notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Shaping for every WAN link on an edge.
 *
 * The link list comes from the health endpoint rather than a second inventory
 * call: it already returns the links this device has, and asking twice would let
 * the two answers disagree about which links exist.
 */
export function DeviceQosPanels({ deviceId }: { deviceId: string }): JSX.Element | null {
  const { data, error, loading } = useAsync(
    () => api.get<Array<{ wanLinkId: string; name: string }>>(`/wan/devices/${deviceId}/health`),
    [deviceId],
  );

  if (loading) return <Loading />;
  if (error || !data || data.length === 0) return null;

  return (
    <>
      {data.map((link) => (
        <QosPanel key={link.wanLinkId} wanLinkId={link.wanLinkId} />
      ))}
    </>
  );
}
